<template>
  <div class="live-room-page">
    <div class="room-shell">
      <div class="lr-bg" :style="backgroundStyle"></div>
      <div class="lr-shade"></div>

      <header class="lr-top">
        <button class="lr-back" type="button" @click="$router.back()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M15 18l-6-6 6-6"></path>
          </svg>
        </button>

        <div class="lr-host">
          <img class="lr-avatar" :src="hostAvatar" :alt="hostName" />
          <span class="lr-host-copy">
            <b>{{ hostName }}</b>
            <span>{{ hostMeta }}</span>
          </span>
          <button class="lr-follow" type="button" :class="{ on: followed }" @click="followed = !followed">
            {{ followed ? copy.followed : copy.follow }}
          </button>
        </div>

        <span class="lr-badge" :class="{ ai: isAiRoom }">{{ badgeText }}</span>
      </header>

      <template v-if="isAiRoom">
        <div ref="chatList" class="lr-chat">
          <div class="lr-chat-inner">
            <div v-if="introMessage" :key="introMessage.id" class="msg-row" :class="introMessage.role">
              <div v-if="introMessage.role === 'assistant'" class="msg-head">{{ hostName }}</div>
              <div class="msg-bubble">{{ introMessage.text }}</div>
            </div>

            <div v-for="item in chatMessages" :key="item.id" class="msg-row" :class="item.role">
              <template v-if="item.type === 'entry'">
                <button class="lr-service-entry" type="button" @click="openAgent(item.link)">
                  <b>{{ item.name }}</b>
                  <span>{{ copy.enterRoom }}</span>
                </button>
              </template>
              <template v-else>
                <div v-if="item.role === 'assistant'" class="msg-head">{{ hostName }}</div>
                <div class="msg-bubble">{{ item.text }}</div>
              </template>
            </div>
          </div>
        </div>

        <div class="lr-bottom">
          <div class="lr-quick">
            <button
              v-for="phrase in quickPhrases"
              :key="phrase"
              class="lr-quick-btn"
              type="button"
              @click="sendMessage(phrase)"
            >
              {{ phrase }}
            </button>
          </div>

          <div class="lr-composer">
            <input
              v-model.trim="draft"
              class="lr-input"
              type="text"
              :placeholder="copy.saySomething"
              @keyup.enter="sendMessage()"
            />
            <button class="lr-send" type="button" @click="sendMessage()">{{ copy.send }}</button>
          </div>

          <div class="lr-actions">
            <button class="lr-action lr-action-primary" type="button" @click="$toast(copy.voiceHint)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9">
                <path d="M12 18a4 4 0 0 0 4-4V8a4 4 0 0 0-8 0v6a4 4 0 0 0 4 4Z"></path>
                <path d="M5 12a7 7 0 0 0 14 0"></path>
                <path d="M12 19v3"></path>
              </svg>
              <span>{{ copy.startVoice }}</span>
            </button>

            <button class="lr-action" type="button" @click="$toast(copy.audioHint)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9">
                <path d="M11 5 6.5 9H3v6h3.5L11 19V5Z"></path>
                <path d="M15.5 8.5a5 5 0 0 1 0 7"></path>
                <path d="M18 6a8.5 8.5 0 0 1 0 12"></path>
              </svg>
              <span>{{ copy.startAudio }}</span>
            </button>

            <button class="lr-icon-btn" type="button" @click="$toast(copy.fullscreenHint)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9">
                <path d="M8 3H3v5"></path>
                <path d="M16 3h5v5"></path>
                <path d="M21 16v5h-5"></path>
                <path d="M8 21H3v-5"></path>
              </svg>
            </button>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="lr-locked-wrap">
          <section class="lr-lock-card">
            <div class="lr-lock-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <rect x="4" y="10" width="16" height="11" rx="3"></rect>
                <path d="M8 10V7a4 4 0 0 1 8 0v3"></path>
                <circle cx="12" cy="15" r="1"></circle>
              </svg>
            </div>
            <h2>{{ copy.lockTitle }}</h2>
            <p>{{ lockDescText }}</p>
            <div class="lr-lock-act">
              <button type="button" @click="$router.back()">{{ copy.backPlaza }}</button>
              <button class="primary" type="button" @click="openUnlockService">{{ copy.unlockNow }}</button>
            </div>
          </section>

          <div class="lr-lock-list">
            <button
              v-for="item in lockEntries"
              :key="item.id"
              class="lr-service-entry"
              type="button"
              @click="openAgent(item.link)"
            >
              <b>{{ item.name }}</b>
              <span>{{ copy.enterRoom }}</span>
            </button>
          </div>
        </div>
      </template>
    </div>

    <van-popup v-model="serviceVisible" class="service-popup" round>
      <div class="service-card">
        <button class="service-close" type="button" @click="serviceVisible = false">×</button>
        <div class="service-icon">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M21.7 3.2 18.5 20c-.2 1.2-.9 1.5-1.9.9l-4.8-3.5-2.3 2.2c-.3.3-.5.5-1 .5l.3-4.9 8.9-8c.4-.3-.1-.5-.6-.2L6.1 13.9 1.4 12.4c-1-.3-1-1 .2-1.5L20 3.8c.9-.3 1.8.2 1.7-.6Z"
            ></path>
          </svg>
        </div>
        <h3>{{ kefuTitle }}</h3>
        <p class="service-sub">{{ copy.serviceDesc }}</p>

        <button
          v-for="agent in serviceAgents"
          :key="`${agent.name}-${agent.link}`"
          class="service-item"
          type="button"
          @click="openAgent(agent.link)"
        >
          <span class="service-item-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M21.7 3.2 18.5 20c-.2 1.2-.9 1.5-1.9.9l-4.8-3.5-2.3 2.2c-.3.3-.5.5-1 .5l.3-4.9 8.9-8c.4-.3-.1-.5-.6-.2L6.1 13.9 1.4 12.4c-1-.3-1-1 .2-1.5L20 3.8c.9-.3 1.8.2 1.7-.6Z"
              ></path>
            </svg>
          </span>
          <span class="service-item-copy">
            <b>{{ agent.name }}</b>
            <small>{{ copy.serviceOnline }}</small>
          </span>
          <em>{{ kefuActionText }}</em>
          <span class="service-arrow">›</span>
        </button>

        <button class="later-btn" type="button" @click="serviceVisible = false">
          {{ copy.serviceLater }}
        </button>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { getPageCopy } from "@/common/pageLocale";
import { getKefuContact, openKefuContact } from "@/common/kefu";

export default {
  data() {
    return {
      detail: null,
      followed: false,
      draft: "",
      serviceVisible: false,
      messages: [],
      lockEntries: [],
      replyTimer: null,
      botTimer: null,
    };
  },
  computed: {
    copy() {
      return getPageCopy(this, "liveRoom");
    },
    baseInfo() {
      return this.$store.getters.getBaseInfo || {};
    },
    kefuContact() {
      return getKefuContact(this.baseInfo);
    },
    kefuTitle() {
      return this.kefuContact.title || this.copy.serviceTitle;
    },
    kefuActionText() {
      return this.kefuContact.action_text || this.copy.serviceAction;
    },
    lockDescText() {
      return String(this.copy.lockDesc || "")
        .replace(/地区或/g, "")
        .replace(/地區或/g, "")
        .replace(/\s{2,}/g, " ")
        .trim();
    },
    introMessage() {
      const firstMessage = this.messages[0] || null;
      if (!firstMessage || firstMessage.type !== "intro" || firstMessage.role !== "assistant") {
        return null;
      }
      return firstMessage;
    },
    chatMessages() {
      return this.introMessage ? this.messages.slice(1) : this.messages;
    },
    isAiRoom() {
      return this.$route.query.mode === "ai";
    },
    hostName() {
      return this.$route.query.title || (this.isAiRoom ? this.copy.aiTitle : "");
    },
    hostMeta() {
      return this.$route.query.viewers
        ? `${this.$route.query.viewers} ${this.copy.hostMeta}`
        : this.copy.hostMeta;
    },
    badgeText() {
      return this.$route.query.badge || (this.isAiRoom ? this.copy.aiBadge : this.copy.liveBadge);
    },
    hostAvatar() {
      if (this.detail && this.detail.img_url) {
        return this.detail.img_url;
      }
      return this.$route.query.image || "";
    },
    backgroundStyle() {
      return this.hostAvatar ? { backgroundImage: `url(${this.hostAvatar})` } : {};
    },
    quickPhrases() {
      return this.copy.quickPhrases || [];
    },
    serviceAgents() {
      const fallback = this.copy.serviceFallback || [];
      const contact = this.kefuContact;
      const defaultLink = contact.url || "";
      const agents = [];

      if (defaultLink) {
        agents.push({
          name: contact.name || this.copy.serviceExclusiveName,
          link: defaultLink,
        });
      }

      fallback.forEach((item) => {
        agents.push({
          ...item,
          link: item.link || defaultLink,
        });
      });

      return agents;
    },
  },
  created() {
    this.fetchDetail();
    this.initMessages();
  },
  mounted() {
    if (this.isAiRoom) {
      this.seedBotEntries();
      this.startBotEntries();
    } else {
      this.seedLockEntries();
      this.startBotEntries();
    }
    this.scrollChatToBottom();
  },
  beforeDestroy() {
    if (this.replyTimer) {
      clearTimeout(this.replyTimer);
      this.replyTimer = null;
    }
    this.stopBotEntries();
  },
  methods: {
    async fetchDetail() {
      const id = this.$route.query.id;
      if (!id) {
        return;
      }
      try {
        const res = await this.$http({
          method: "get",
          url: "xuanfeidata",
          data: { id },
        });
        this.detail = res && res.data ? res.data : null;
      } catch (error) {
        this.detail = null;
      }
    },
    initMessages() {
      if (!this.isAiRoom) {
        this.messages = [];
        return;
      }
      this.messages = [
        {
          id: `assistant-${Date.now()}`,
          type: "intro",
          role: "assistant",
          text: this.copy.aiWelcome,
        },
      ];
    },
    seedBotEntries() {
      for (let index = 0; index < 4; index += 1) {
        this.appendBotEntry(false);
      }
      this.scrollChatToBottom();
    },
    seedLockEntries() {
      this.lockEntries = [];
      for (let index = 0; index < 4; index += 1) {
        this.appendLockEntry();
      }
    },
    startBotEntries() {
      this.stopBotEntries();
      const scheduleNext = () => {
        const delay = 900 + Math.floor(Math.random() * 1600);
        this.botTimer = setTimeout(() => {
          if (this.isAiRoom) {
            this.appendBotEntry();
          } else {
            this.appendLockEntry();
          }
          scheduleNext();
        }, delay);
      };
      scheduleNext();
    },
    stopBotEntries() {
      if (this.botTimer) {
        clearTimeout(this.botTimer);
        this.botTimer = null;
      }
    },
    appendBotEntry(shouldScroll = true) {
      const names = this.copy.botNames || [];
      const name = names[Math.floor(Math.random() * names.length)] || this.copy.newUserName;
      const primaryAgent = this.serviceAgents[0] || { link: "" };
      this.messages.push({
        id: `entry-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        type: "entry",
        name,
        link: primaryAgent.link || "",
      });
      this.trimMessages();
      if (shouldScroll) {
        this.scrollChatToBottom();
      }
    },
    appendLockEntry() {
      const names = this.copy.botNames || [];
      const name = names[Math.floor(Math.random() * names.length)] || this.copy.newUserName;
      const primaryAgent = this.serviceAgents[0] || { link: "" };
      this.lockEntries.push({
        id: `lock-entry-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        name,
        link: primaryAgent.link || "",
      });
      if (this.lockEntries.length > 4) {
        this.lockEntries = this.lockEntries.slice(-4);
      }
    },
    trimMessages() {
      const intro = this.messages.find((item) => item.type === "intro") || null;
      const others = this.messages.filter((item) => item.type !== "intro");
      let nextMessages = others.slice(-4);

      if (intro && others.length <= 4) {
        nextMessages = [intro].concat(nextMessages);
      }

      this.messages = nextMessages;
    },
    trimAllMessages() {
      this.trimMessages();
    },
    sendMessage(rawText) {
      if (!this.isAiRoom) {
        return;
      }
      const text = typeof rawText === "string" ? rawText : this.draft;
      if (!text || !String(text).trim()) {
        return;
      }
      const messageText = String(text).trim();
      this.messages.push({
        id: `user-${Date.now()}`,
        type: "message",
        role: "user",
        text: messageText,
      });
      this.trimAllMessages();
      this.draft = "";
      this.scrollChatToBottom();

      if (this.replyTimer) {
        clearTimeout(this.replyTimer);
      }
      this.replyTimer = setTimeout(() => {
        this.messages.push({
          id: `assistant-${Date.now()}`,
          type: "message",
          role: "assistant",
          text: this.pickReply(messageText),
        });
        this.trimAllMessages();
        this.scrollChatToBottom();
      }, 520);
    },
    pickReply(text) {
      const value = text.toLowerCase();
      if (/你好|hello|hi|嗨|在吗|在嗎/.test(value)) {
        return this.copy.greetingReply;
      }
      if (/心情|how are|怎麼樣|怎么样/.test(value)) {
        return this.copy.moodReply;
      }
      if (/约|聊|date|喜欢|喜歡/.test(value)) {
        return this.copy.dateReply;
      }
      return this.copy.fallbackReply;
    },
    scrollChatToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatList;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    },
    async openUnlockService() {
      const opened = await openKefuContact(this, { skipFallbackRoute: true });
      if (opened) {
        return;
      }
      this.serviceVisible = true;
    },
    openAgent(link) {
      openKefuContact(this, { url: link });
    },
  },
};
</script>

<style lang="less" scoped>
.live-room-page {
  width: 100%;
  height: 100vh;
  height: 100dvh;
  min-height: 100vh;
  min-height: 100dvh;
  overflow: hidden;
  background: #05030a;
  color: #fff;
}

.room-shell {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  height: 100vh;
  height: 100dvh;
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0 auto;
  overflow: hidden;
  background: #05030a;
}

.lr-bg,
.lr-shade {
  position: absolute;
  inset: 0;
}

.lr-bg {
  background-color: #06030d;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  filter: blur(0.1rem);
  transform: scale(1.04);
}

.lr-shade {
  background:
    linear-gradient(180deg, rgba(4, 3, 8, 0.48) 0%, rgba(4, 3, 8, 0.52) 24%, rgba(4, 3, 8, 0.82) 100%),
    radial-gradient(circle at 50% 100%, rgba(165, 92, 255, 0.08), transparent 30%);
}

.lr-top,
.lr-chat,
.lr-bottom,
.lr-locked-wrap {
  position: relative;
  z-index: 1;
}

.lr-top {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.7rem;
  padding: max(0.75rem, env(safe-area-inset-top)) 0.75rem 0;
}

.lr-back,
.lr-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.45rem;
  height: 2.45rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 50%;
  background: rgba(10, 8, 17, 0.42);
  color: #fff;
}

.lr-back svg,
.lr-icon-btn svg {
  width: 1.2rem;
  height: 1.2rem;
}

.lr-host {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 0.58rem;
  min-height: 2.7rem;
  padding: 0 0.12rem 0 0;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  background: rgba(9, 7, 14, 0.38);
}

.lr-avatar {
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 120, 170, 0.88);
}

.lr-host-copy {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.lr-host-copy b {
  display: block;
  font-size: 0.78rem;
  font-weight: 900;
}

.lr-host-copy span {
  display: block;
  margin-top: 0.08rem;
  color: rgba(255, 255, 255, 0.74);
  font-size: 0.56rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lr-follow {
  min-width: 3.3rem;
  height: 2rem;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #ff5d98, #ff3f77);
  color: #fff;
  font-size: 0.66rem;
  font-weight: 900;
}

.lr-follow.on {
  background: rgba(255, 255, 255, 0.14);
}

.lr-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 3.45rem;
  height: 2.2rem;
  padding: 0 0.75rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #ff5b83, #ff6c68);
  font-size: 0.68rem;
  font-weight: 900;
}

.lr-badge.ai {
  background: linear-gradient(135deg, #ff6488, #ff7d72);
}

.lr-chat {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 0.65rem;
  padding: 0 0.75rem;
  overflow-y: auto;
  scrollbar-width: none;
}

.lr-chat::-webkit-scrollbar {
  display: none;
}

.lr-chat-inner {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  margin-top: auto;
  min-height: min-content;
}

.msg-row {
  display: flex;
  flex-direction: column;
  max-width: 84%;
}

.msg-row.user {
  align-self: flex-end;
}

.msg-row.assistant {
  align-self: flex-start;
}

.msg-head {
  margin: 0 0 0.16rem 0.3rem;
  color: rgba(255, 142, 188, 0.82);
  font-size: 0.58rem;
  font-weight: 700;
}

.msg-bubble {
  padding: 0.74rem 0.88rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1rem;
  background: rgba(27, 14, 38, 0.9);
  font-size: 0.74rem;
  line-height: 1.4;
}

.msg-row.user .msg-bubble {
  border-color: rgba(164, 98, 255, 0.32);
  background: rgba(62, 31, 100, 0.74);
}

.lr-service-entry {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  width: fit-content;
  max-width: 78%;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.92rem;
  background: rgba(13, 8, 20, 0.74);
  color: #fff;
  text-align: left;
}

.lr-service-entry b {
  color: #ff7db0;
  font-size: 0.66rem;
  font-weight: 900;
}

.lr-service-entry span {
  color: rgba(255, 255, 255, 0.86);
  font-size: 0.7rem;
  font-weight: 800;
}

.lr-bottom {
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  padding: 0.55rem 0.75rem max(0.75rem, env(safe-area-inset-bottom));
}

.lr-quick {
  display: flex;
  gap: 0.52rem;
  overflow-x: auto;
  padding-bottom: 0.2rem;
  scrollbar-width: none;
}

.lr-quick::-webkit-scrollbar {
  display: none;
}

.lr-quick-btn {
  flex: 0 0 auto;
  min-width: 3.7rem;
  height: 2rem;
  padding: 0 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  background: rgba(14, 9, 21, 0.8);
  color: #fff;
  font-size: 0.64rem;
  font-weight: 800;
}

.lr-composer {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.6rem;
  margin-top: 0.65rem;
}

.lr-input {
  width: 100%;
  height: 3.1rem;
  padding: 0 1rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  background: rgba(14, 9, 21, 0.9);
  color: #fff;
  font-size: 0.74rem;
}

.lr-send {
  min-width: 3.15rem;
  height: 3.1rem;
  border: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, #bd63ff, #ff4c8b);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 900;
}

.lr-actions {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 0.6rem;
  margin-top: 0.65rem;
}

.lr-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  min-height: 3rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  background: rgba(11, 7, 18, 0.88);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 800;
}

.lr-action svg {
  width: 1rem;
  height: 1rem;
}

.lr-action-primary {
  border: 0;
  background: linear-gradient(135deg, #b85fff, #ff4e8a);
}

.lr-locked-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 0;
  padding: 0 0.75rem 1rem;
}

.lr-lock-card {
  width: 100%;
  padding: 1.3rem 1rem 1rem;
  border: 1px solid rgba(255, 95, 151, 0.18);
  border-radius: 1.85rem;
  background: rgba(29, 13, 41, 0.92);
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.28);
  text-align: center;
}

.lr-lock-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 4.1rem;
  height: 4.1rem;
  border: 1px solid rgba(255, 117, 175, 0.45);
  border-radius: 1.2rem;
  background: rgba(74, 33, 92, 0.82);
  color: #ff93be;
}

.lr-lock-icon svg {
  width: 1.75rem;
  height: 1.75rem;
}

.lr-lock-card h2 {
  margin: 1rem 0 0;
  font-size: 1.42rem;
  font-weight: 900;
}

.lr-lock-card p {
  margin: 0.65rem 0 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.78rem;
  line-height: 1.65;
}

.lr-lock-act {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 0.7rem;
  margin-top: 1rem;
}

.lr-lock-act button {
  min-height: 3.2rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  font-size: 0.78rem;
  font-weight: 800;
}

.lr-lock-act .primary {
  border: 0;
  background: linear-gradient(135deg, #ff709e, #ff4f87);
}

.lr-lock-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 1rem;
}

.service-popup {
  width: min(21.5rem, calc(100vw - 2rem));
  border-radius: 1.55rem;
  background: linear-gradient(180deg, rgba(38, 18, 47, 0.98), rgba(22, 12, 32, 0.98));
}

.service-card {
  position: relative;
  padding: 1.2rem 1rem 1rem;
}

.service-close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 2rem;
  height: 2rem;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.72);
  font-size: 1rem;
}

.service-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.7rem;
  height: 3.7rem;
  margin: 0 auto 0.9rem;
  border-radius: 1rem;
  background: linear-gradient(135deg, #37c0ff, #3490ff);
}

.service-icon svg,
.service-item-icon svg {
  width: 1.7rem;
  height: 1.7rem;
}

.service-card h3 {
  margin: 0;
  text-align: center;
  font-size: 1.06rem;
  font-weight: 900;
}

.service-sub {
  margin: 0.7rem auto 0;
  max-width: 17.5rem;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.68rem;
  line-height: 1.55;
  text-align: center;
}

.service-item {
  display: flex;
  align-items: center;
  gap: 0.72rem;
  width: 100%;
  margin-top: 0.72rem;
  padding: 0.85rem 0.9rem;
  border: 1px solid rgba(255, 93, 149, 0.35);
  border-radius: 1rem;
  background: rgba(28, 18, 36, 0.92);
  color: #fff;
  text-align: left;
}

.service-item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.7rem;
  height: 2.7rem;
  border-radius: 0.85rem;
  background: linear-gradient(135deg, #37c0ff, #3490ff);
  color: #fff;
  flex-shrink: 0;
}

.service-item-copy {
  min-width: 0;
  flex: 1;
}

.service-item-copy b {
  display: block;
  font-size: 0.8rem;
  font-weight: 800;
}

.service-item-copy small {
  display: block;
  margin-top: 0.18rem;
  color: #74ffc4;
  font-size: 0.58rem;
}

.service-item em {
  color: #ff77aa;
  font-size: 0.64rem;
  font-style: normal;
  font-weight: 800;
}

.service-arrow {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.84rem;
}

.later-btn {
  width: 100%;
  min-height: 2.85rem;
  margin-top: 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.9rem;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.76rem;
  font-weight: 800;
}

button,
input {
  cursor: pointer;
}

@media (min-width: 48rem) {
  .room-shell {
    width: 23.4375rem;
    max-width: 23.4375rem;
  }
}
</style>
